import { endEvent as endEventAPI } from '@goldcast/api/backend';

import { EventContext } from './actions';
import { getAgendaItemType } from './getters';

import type { AgendaItem, Broadcast, TrackItem, AgendaItemType, User } from './types';

import analytics from '@/analytics';
import i18n from '@/i18n';
import { getBroadcastDetails } from '@/services/event.service';
import loggerService from '@/services/logger.service';
import { showToast } from '@/services/toast.service';
import { makeUnique } from '@/services/utils';
import store from '@/store';
import { scheduleUserFetch } from '@/store/schedulers';
import {
  getTotalVideoDurationOfBroadcast,
  getSimuliveTestRunEndTime,
  getSessionEndTimeBasedOnTotalVideoDuration,
  getSimuliveTestVideoStartTime
} from '@/utils/eventActionUtils';
import { pushRoute } from '@/utils/routerAccess';
import { makeUser } from '@/utils/userTransform';
import { extractErrorMessage } from '@/utils/utils';

export function formatAgendaItem(
  item: AgendaItem | Broadcast,
  tracks: TrackItem[],
  users: User[]
): AgendaItem | Broadcast {
  const type = getAgendaItemType(item);
  return type === 'BROADCAST' ? makeBroadcast(item) : makeAgendaItem(item, tracks, type);

  function getSpeakerDataByIdFromUber(id: string) {
    return store.state.event.event.rawSpeakersFromUber.find((sp: { id: String }) => sp.id === id);
  }

  function makeBroadcast(item: any): Broadcast {
    const durationOfAllVideoAssets = getTotalVideoDurationOfBroadcast(item);
    const expectedSimuliveTestRunVideoEndTime = getSimuliveTestRunEndTime(item);
    // TODO: Refactor this abomination
    return {
      ...item,
      ...makeAgendaItem(item, tracks, type),
      durationOfAllVideoAssets: durationOfAllVideoAssets,
      expectedSimuliveVideoEndTime: getSessionEndTimeBasedOnTotalVideoDuration(item, durationOfAllVideoAssets),
      expectedSimuliveTestRunVideoEndTime: expectedSimuliveTestRunVideoEndTime,
      expectedSimuliveTestRunVideoStartTime: getSimuliveTestVideoStartTime(
        expectedSimuliveTestRunVideoEndTime,
        durationOfAllVideoAssets
      ),
      speakerIds: item.speakers?.map((speaker: any) => speaker?.id || speaker) || [],
      is_available_on_demand: store.state.event.event?.is_available_on_demand && item.is_available_on_demand,
      on_demand_asset:
        item.on_demand_asset ||
        store.state.event.event?.agenda?.broadcasts?.find((broadcast: Broadcast) => broadcast.id === item.id)
          ?.on_demand_asset,
      speakers: item.speakers
        ?.map((speaker: any) => {
          let speakerData;
          if (!speaker.id) {
            speakerData = getSpeakerDataByIdFromUber(speaker);
          }
          let user = users[speaker.id || speaker];
          if (!user) {
            scheduleUserFetch(speaker.id || speaker, false);

            if (speaker.id) {
              user = makeUser({
                user: speaker,
                id: '',
                event: item.event,
                blocked: false,
                view_as_attendee: false
              });
            }
          }
          if (speakerData) {
            return { ...user, ...speakerData };
          }
          if (!speakerData && !user) {
            return false;
          }
          return { ...user, ...speaker };
        })
        .filter(Boolean)
        .sort((a: any, b: any) => {
          if (a.rank && b.rank) {
            return a.rank - b.rank;
          }

          return getSpeakerDataByIdFromUber(a.id)?.rank - getSpeakerDataByIdFromUber(b.id)?.rank;
        })
    };
  }
}

export function makeAgendaItem(item: any, tracks: TrackItem[], type: AgendaItemType): AgendaItem {
  const itemTracks = tracks.filter(track => track.agenda_items.includes(`${item.id}`));
  const tickets: string[] = makeUnique(
    itemTracks.flatMap(track => track.tickets.map(({ code, label }) => label || code))
  );
  const disabled = itemTracks.length > 0 && itemTracks.every(track => track.disabled);

  return {
    ...item,
    tickets,
    tracks: itemTracks.length > 1 ? itemTracks.map(({ title }) => title) : [],
    availableTracks: tracks.length > 1 ? itemTracks.map(({ title }) => title) : [],
    disabled,
    type
  };
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export async function fetchAndDispatchBroadcastDetails(event: any, eventID: string): Promise<void | null> {
  if (event?.event_type !== 'Recording') {
    return;
  }

  const RETREIVE_BROADCAST_MAX_ATTEMPTS = 3;
  const baseDelay = 1000;
  const maxDelay = 5000;
  let broadcastDetails: Broadcast[] | null = null;

  for (let attempt = 1; attempt <= RETREIVE_BROADCAST_MAX_ATTEMPTS; attempt++) {
    try {
      const response = await getBroadcastDetails(eventID);
      broadcastDetails = response.data;

      if (Array.isArray(broadcastDetails) && broadcastDetails.length > 0) {
        store.dispatch('event/handleAgendaUpdate', {
          item: broadcastDetails[0],
          isBroadcast: true
        });
        break;
      } else {
        loggerService.error('Broadcast details returned empty or invalid data for recording event', {
          eventID,
          broadcastDetails
        });
        throw new Error(`Invalid broadcast details data for event ID: ${eventID}`);
      }
    } catch (e) {
      if (attempt === RETREIVE_BROADCAST_MAX_ATTEMPTS) {
        loggerService.error('Get broadcast details failed after 3 attempts.', e);
      } else {
        const delay = Math.min(baseDelay * 2 ** (attempt - 1), maxDelay);
        loggerService.warn(`Retrying getBroadcastDetails in ${delay}ms (attempt ${attempt})`, e);
        await sleep(delay);
      }
    }
  }
}

export async function endEvent(eventID: string) {
  try {
    await endEventAPI({ id: eventID });
    analytics.track('eventEnded', { event_id: eventID });
  } catch (error) {
    const errorMessage = extractErrorMessage(error, 'Failed to end event. Please try again.');
    loggerService.error('Failed to end event', { error });
    showToast({ message: errorMessage, type: 'error', dismissible: true });
  }
}

export async function moveToEndEvent(eventID: string) {
  if (isUserProducerOrBroadcastStaff()) {
    const isProducer = store.getters['isOrganizerV2'];

    const message = i18n?.global?.t(
      `@@translate@@ This event has ended. All attendees have been removed from the event space. As a {role} you may remain inside the event space.`,
      { role: isProducer ? 'staff member' : 'speaker' }
    );
    showToast({
      message,
      type: 'info',
      dismissible: true
    });
    loggerService.log('User is producer or broadcast staff, skipping exit flow');
    return;
  }
  try {
    store
      .dispatch('event/logout')
      .then(() => {
        pushRoute({ name: 'EndEvent', query: { eventID: eventID } });
      })
      .catch(error => {
        loggerService.error('Failed to logout', { error });
      });
  } catch (error) {
    loggerService.error('Failed to move to end event', { error });
  }
}

const isUserProducerOrBroadcastStaff = () => {
  const currentBroadcast = store.getters['event/currentBroadcast'] as Broadcast | null;
  const eventID = store.state.event.event?.id;
  const permissions = store.state.user.permissions;
  return !!permissions?.events[eventID || '']?.length || !!permissions?.broadcasts[currentBroadcast?.id || '']?.length;
};
